
/**
 * React Query hook for SMS Templates
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SmsTemplate } from '@/hooks/sms/types';
import { createTemplate, deleteTemplate, updateTemplate } from '@/services/smsApi';
import SmsService from '@/api/sms';
import { UserProfile } from '@/api/auth';
import { isFeatureEnabled } from '@/utils/features/featureFlags';
import { toast } from '@/hooks/use-toast';

/**
 * Hook for fetching SMS templates
 */
export function useSmsTemplates(
  user: UserProfile | null | undefined,
  selectedRestaurantIds: string[] = []
) {
  // Don't execute if feature flag is disabled
  const enabled = isFeatureEnabled('enable_react_query') && !!user;
  
  // Determine which restaurant ID to use
  const restaurantId = selectedRestaurantIds.length > 0 && !selectedRestaurantIds.includes('all')
    ? selectedRestaurantIds[0]
    : user?.restaurants?.[0]?.id;
  
  return useQuery({
    queryKey: ['smsTemplates', restaurantId],
    queryFn: async () => {
      if (!restaurantId) {
        console.warn('No restaurant selected for template fetching');
        throw new Error('No restaurant selected');
      }
      
      console.log(`Fetching templates for restaurant: ${restaurantId}`);
      
      try {
        const templates = await SmsService.fetchTemplates(restaurantId);
        console.log(`Successfully fetched ${templates.length} templates`);
        
        // Log the first template for debugging if available
        if (templates.length > 0) {
          console.log('Sample template:', {
            id: templates[0].id,
            name: templates[0].name,
            restaurant_id: templates[0].restaurant_id,
            organisation_id: templates[0].organisation_id || 'N/A'
          });
        }
        
        return templates;
      } catch (error) {
        console.error('Error fetching templates:', error);
        // Return empty array instead of throwing to prevent UI crashes
        toast({
          title: "Failed to load templates",
          description: error instanceof Error ? error.message : "An unknown error occurred",
          variant: "destructive"
        });
        return [];
      }
    },
    enabled: enabled && !!restaurantId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
  });
}

/**
 * Hook for creating SMS templates
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (templateData: any) => {
      return await createTemplate(templateData);
    },
    onSuccess: (data, variables) => {
      // Invalidate templates for this restaurant
      if (variables.restaurant_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['smsTemplates', variables.restaurant_id] 
        });
      }
      
      toast({
        title: "Template created",
        description: "Your message template has been saved successfully."
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to create template",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    }
  });
}

/**
 * Hook for updating SMS templates
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ templateId, data }: { templateId: string; data: any }) => {
      return await updateTemplate(templateId, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate templates for this restaurant
      if (variables.data.restaurant_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['smsTemplates', variables.data.restaurant_id] 
        });
      }
      
      toast({
        title: "Template updated",
        description: "Your message template has been updated successfully."
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to update template",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    }
  });
}

/**
 * Hook for deleting SMS templates
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ templateId, restaurantId }: { templateId: string; restaurantId: string }) => {
      return await deleteTemplate(templateId);
    },
    onSuccess: (_, variables) => {
      // Invalidate templates for this restaurant
      queryClient.invalidateQueries({ 
        queryKey: ['smsTemplates', variables.restaurantId] 
      });
      
      toast({
        title: "Template deleted",
        description: "The template has been deleted successfully."
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to delete template",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    }
  });
}

/**
 * Hook that combines fetching, creation, updating and deletion
 */
export function useSmsTemplatesManager(
  user: UserProfile | null | undefined,
  selectedRestaurantIds: string[] = []
) {
  const queryClient = useQueryClient();
  const templatesQuery = useSmsTemplates(user, selectedRestaurantIds);
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const deleteMutation = useDeleteTemplate();
  
  // Function to manually refresh templates
  const refreshTemplates = () => {
    const restaurantId = selectedRestaurantIds.length > 0 && !selectedRestaurantIds.includes('all')
      ? selectedRestaurantIds[0]
      : user?.restaurants?.[0]?.id;
    
    if (restaurantId) {
      queryClient.invalidateQueries({ 
        queryKey: ['smsTemplates', restaurantId]
      });
    }
  };
  
  return {
    templates: templatesQuery.data as SmsTemplate[] || [],
    isLoading: templatesQuery.isLoading,
    isError: templatesQuery.isError,
    error: templatesQuery.error,
    refreshTemplates,
    createTemplate: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateTemplate: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteTemplate: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending
  };
}
